/* The bend list, as a cart panel at the right. From the v15 prototype and the Figma Cart Row
   (Bend-Creator nodes 61:9709 and 61:9753). Tokens come from the shell (`--bcs-*`). */
.panel {
  width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--bcs-hairline);
  border-radius: 10px;
  box-shadow: var(--bcs-panel-shadow);
  animation: slideIn 0.26s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(16px);
  }
}

/* Collapsed: a small button with a count. */
.pill {
  height: 56px;
  padding: 0 12px 0 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--bcs-hairline);
  border-radius: 10px;
  box-shadow: var(--bcs-panel-shadow);
  font-size: 15px;
  font-weight: 600;
  animation: slideIn 0.26s ease;

  &:hover {
    background: var(--bcs-control-hover);
  }
}

.badge {
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 14px;
  background: var(--bcs-shop-primary);
  color: #fff;
  font-family: var(--bcs-mono);
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header {
  flex: 0 0 auto;
  height: 64px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border-bottom: 1px solid var(--bcs-gray-2);
}

.headerTitle {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
}

.collapseButton {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  color: var(--bcs-gray-7);
}

.body {
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.row {
  padding: 16px;
  border-bottom: 1px solid var(--bcs-gray-2);
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  cursor: pointer;
  transition: background 100ms ease;

  &:hover {
    background: var(--bcs-control-hover);
  }

  /* Another row is open, so this one can't be selected and gives no tap feedback. */
  &[data-locked] {
    cursor: default;

    &:hover {
      background: none;
    }
  }

  /* Selected, open or being edited: white, marked only by the 3px accent edge. */
  &[data-selected],
  &[data-expanded] {
    background: #fff;
    box-shadow: inset 3px 0 0 var(--bcs-shop-primary);

    &:hover {
      background: #fff;
    }
  }

  /* Just added: flash the tint, hold it, then fade it into the row's own state, white when selected or open. */
  &[data-new] {
    animation: newFade 2.4s ease-out both;
  }

  &[data-new]:is([data-selected], [data-expanded]) {
    animation-name: newSelectedFade;
  }
}

@keyframes newFade {
  0%,
  35% {
    background: var(--mantine-primary-color-light);
  }
  100% {
    background: transparent;
  }
}

@keyframes newSelectedFade {
  0%,
  35% {
    background: var(--mantine-primary-color-light);
  }
  100% {
    background: #fff;
  }
}

@keyframes newDetailsFade {
  0%,
  35% {
    background: #fff;
  }
  100% {
    background: rgba(0, 0, 0, 0.05);
  }
}

.titleLine {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 18px;
}

.number {
  font-family: var(--bcs-mono);
  color: var(--bcs-gray-6);
}

.name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--bcs-shop-primary);

  &[data-invalid] {
    color: var(--mantine-color-red-8);
  }
}

.rowButton {
  width: 30px;
  height: 30px;
  min-width: 30px;
  background: #fff;
  border: 1px solid var(--bcs-border-control);
  border-radius: 6px;
  color: var(--bcs-t87);

  &:hover {
    background: var(--bcs-control-hover);
  }

  /* Edit stays lit while its row is open. */
  &[data-active] {
    border-color: var(--bcs-shop-primary);
    background: var(--mantine-primary-color-light);
    color: var(--bcs-shop-primary);
  }
}

.details {
  padding: 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.05);
  font-family: var(--bcs-sans-condensed);
  font-size: 13px;
  line-height: 17px;
  color: var(--bcs-gray-6);

  .row[data-selected] & {
    background: #fff;
  }

  .row[data-new]:not([data-selected], [data-expanded]) & {
    animation: newDetailsFade 2.4s ease-out both;
  }
}

.actionLine {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.smallButton {
  height: 40px;
  padding: 0 14px;
  font-size: 14px;
}

/* An open row: one card of 44px jump rows, the name first, then one row per step. */
.jumpList {
  background: #fff;
  border: 1px solid var(--bcs-border-control);
  border-radius: 8px;
  overflow: hidden;
}

.jumpRow {
  width: 100%;
  height: 44px;
  padding: 0 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  background: #fff;
  border-bottom: 1px solid var(--bcs-border-control);

  &:last-child {
    border-bottom: 0;
  }

  &:active {
    background: var(--bcs-control-hover);
  }

  /* The step open in the nav panel for this bend. */
  &[data-active] {
    background: var(--mantine-primary-color-light);

    .jumpLabel {
      color: var(--bcs-shop-primary);
      font-weight: 600;
    }
  }
}

.nameRow {
  cursor: text;

  &:focus-within {
    box-shadow: inset 0 0 0 2px var(--bcs-shop-primary);
  }
}

.jumpLabel {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 18px;
  color: var(--bcs-gray-6);
}

.jumpValue {
  max-width: 130px;
  font-family: var(--bcs-mono);
  font-size: 13px;
  font-weight: 500;
  line-height: 17px;
  color: var(--bcs-gray-9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}

.nameInput {
  width: 130px;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: right;
  outline: none;
}

.chevron {
  flex: 0 0 auto;
  color: var(--mantine-color-gray-5);
}

.footer {
  flex: 0 0 auto;
  border-top: 1px solid var(--bcs-gray-2);
  padding: 16px;
  display: grid;
  gap: 16px;
}

.footerButton {
  width: 100%;
  height: 52px;
  font-size: 15px;
}

/* Fast Track: the label and caption at the left, the switch at the right, as in the prototype's footer. */
.fastTrackBody {
  align-items: center;
}

.fastTrackLabelWrapper {
  flex: 1;
}

.fastTrackLabel {
  padding: 0;
  font-size: 15px;
  font-weight: 500;
  color: var(--bcs-gray-9);
}

.fastTrackDescription {
  margin-top: 2px;
  padding: 0;
  font-size: 13px;
  line-height: 17px;
  color: var(--bcs-gray-7);
}
